import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { User, Mail, Lock, LogIn, UserPlus, Loader2, GraduationCap, Users, School, Phone, Building2 } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth, ROLES } from "../context/AuthContext";

const roleIcons = { student: GraduationCap, counsellor: Users, school: School };
const SIGNUP_ROLES = ROLES.filter((r) => r.value !== "school");

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, googleSignIn } = useAuth();
  const [mode, setMode] = useState(location.state?.mode === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "student", school_id: "" });
  const [schools, setSchools] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/schools/public`).then((r) => setSchools(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "signup") {
        const payload = { ...form };
        if (!payload.school_id) delete payload.school_id;
        await register(payload);
      } else await login({ email: form.email, password: form.password });
      navigate("/account");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#16244F] to-[#2b3f7a] px-5 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 md:p-10">
        <div className="flex justify-center mb-6"><Logo height={48} /></div>

        <div className="grid grid-cols-2 gap-2 p-1 bg-[#f6f8fc] rounded-xl mb-7">
          <button onClick={() => setMode("login")} className={`py-2.5 rounded-lg font-head font-semibold text-sm transition-colors ${mode === "login" ? "bg-white text-[#16244F] shadow-sm" : "text-[#8a90a0]"}`}>Login</button>
          <button onClick={() => setMode("signup")} className={`py-2.5 rounded-lg font-head font-semibold text-sm transition-colors ${mode === "signup" ? "bg-white text-[#16244F] shadow-sm" : "text-[#8a90a0]"}`}>Create Account</button>
        </div>

        <h1 className="font-head text-2xl font-bold text-[#16244F] text-center">{mode === "signup" ? "Create your account" : "Welcome back"}</h1>
        <p className="text-center text-[#8a90a0] text-sm mt-1 mb-6">{mode === "signup" ? "Join as a Student, Counsellor or School/College" : "Sign in to your account"}</p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <>
              <div>
                <label className="text-xs font-head font-semibold text-[#5a6274] mb-2 block">I am a</label>
                <div className="grid grid-cols-2 gap-2">
                  {SIGNUP_ROLES.map((r) => {
                    const Icon = roleIcons[r.value];
                    const active = form.role === r.value;
                    return (
                      <button type="button" key={r.value} onClick={() => set("role", r.value)}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all text-xs font-semibold ${active ? "border-[#2b8cf0] bg-[#eaf3ff] text-[#16244F]" : "border-gray-100 text-[#8a90a0] hover:border-gray-200"}`}>
                        <Icon size={20} className={active ? "text-[#2b8cf0]" : ""} />
                        <span className="text-center leading-tight">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
                <input value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Full name" className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              </div>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} required pattern="[0-9+\-\s]{7,15}" title="Enter a valid mobile number" placeholder="Mobile number" className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
              </div>
              {form.role === "student" && (
                <div className="relative">
                  <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
                  <select value={form.school_id} onChange={(e) => set("school_id", e.target.value)} data-testid="signup-school-select" className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition text-[#16244F] bg-white">
                    <option value="">My School / College (optional)</option>
                    {schools.map((s) => <option key={s.id} value={s.id}>{s.school_name}</option>)}
                  </select>
                </div>
              )}
            </>
          )}
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required placeholder="Email" className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
          </div>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
            <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required placeholder={mode === "signup" ? "Password (min 6 characters)" : "Password"} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] focus:ring-2 focus:ring-[#2b8cf0]/20 outline-none transition" />
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          {mode === "login" && (
            <div className="text-right -mt-1">
              <button type="button" onClick={() => navigate("/forgot-password")} className="text-sm text-[#2b8cf0] hover:underline">Forgot password?</button>
            </div>
          )}
          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-70">
            {loading ? <Loader2 size={18} className="animate-spin" /> : mode === "signup" ? <UserPlus size={18} /> : <LogIn size={18} />}
            {loading ? "Please wait..." : mode === "signup" ? "Create Account" : "Sign In"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-gray-100" />
          <span className="text-xs text-[#8a90a0]">or</span>
          <div className="h-px flex-1 bg-gray-100" />
        </div>
        <button onClick={() => googleSignIn(form.role)} className="w-full inline-flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-50 text-[#16244F] font-head font-semibold py-3 rounded-lg transition-colors">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 35.5 44 30.2 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <p className="text-center text-sm text-[#5a6274] mt-6">
          {mode === "signup" ? "Already have an account? " : "New here? "}
          <button onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-[#F58220] font-semibold">
            {mode === "signup" ? "Log in" : "Create one"}
          </button>
        </p>
        <button onClick={() => navigate("/")} className="block mx-auto mt-4 text-xs text-[#8a90a0] hover:text-[#16244F]">← Back to home</button>
        <div className="mt-5 pt-5 border-t border-gray-100 text-center">
          <p className="text-xs text-[#8a90a0]">Are you a School / College?</p>
          <Link to="/school/register" data-testid="to-school-portal" className="inline-flex items-center gap-1.5 mt-1 text-sm font-semibold text-[#16244F] hover:text-[#F58220]">
            <Building2 size={15} /> Go to School / College Portal
          </Link>
        </div>
      </div>
    </div>
  );
}
